import { ExternalLink, FileText } from "lucide-react";
import { api } from "@/services/api";
import type { EnrollmentWithUser } from "@/types/api";
import { formatCurrency, getAdminPaymentStatusStyle } from "@/utils/payment";

interface PaymentStatusCellProps {
  enrollment: EnrollmentWithUser;
  onRefresh: () => Promise<void>;
}

export function PaymentStatusCell({ enrollment, onRefresh }: PaymentStatusCellProps) {
  const handleViewReceipt = async () => {
    if (!enrollment.payment_id) return;

    try {
      const { receipt_url } = await api.getPaymentReceiptUrl(enrollment.payment_id);
      window.open(receipt_url, "_blank");
    } catch (err) {
      console.error("Failed to get receipt:", err);
    }
  };

  const handleResumePayment = async () => {
    if (!enrollment.payment_id) return;

    try {
      const { checkout_url } = await api.getPaymentCheckoutUrl(enrollment.payment_id);
      window.open(checkout_url, "_blank");
    } catch (err) {
      console.error("Failed to get checkout URL:", err);
      if (err instanceof Error && err.message.includes("expired")) {
        await onRefresh();
      }
    }
  };

  if (!enrollment.payment_status) {
    return <span className="text-gray-400 text-xs">Free</span>;
  }

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2">
        <span
          className={`px-2 py-0.5 text-xs font-medium rounded-full inline-block ${getAdminPaymentStatusStyle(
            enrollment.payment_status
          )}`}
        >
          {enrollment.payment_status}
        </span>

        {/* Receipt button for completed payments */}
        {enrollment.payment_status === "completed" && enrollment.payment_id && (
          <button
            onClick={handleViewReceipt}
            className="text-gray-500 hover:text-gray-700 transition-colors"
            title="View Receipt"
          >
            <FileText className="w-4 h-4" />
          </button>
        )}

        {/* Resume button for pending payments */}
        {enrollment.payment_status === "pending" && enrollment.payment_id && (
          <button
            onClick={handleResumePayment}
            className="text-yellow-600 hover:text-yellow-800 transition-colors"
            title="Resume Payment"
          >
            <ExternalLink className="w-4 h-4" />
          </button>
        )}
      </div>

      {enrollment.payment_amount != null && (
        <span className="text-xs text-gray-500">
          {formatCurrency(enrollment.payment_amount, enrollment.payment_currency ?? "gbp")}
        </span>
      )}
    </div>
  );
}
